import { useState, useEffect } from 'react'
import { Plus, Pencil, UserX, UserCheck, X, Check, Users } from 'lucide-react'
import { teamMembersApi } from '@api/client'
import type { TeamMember } from '@api/client'
import { showToast } from '@store/uiStore'

const ROLE_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'presales', label: 'Presales' },
  { value: 'product_manager', label: 'Product Manager' },
  { value: 'sales', label: 'Sales' },
  { value: 'account_manager', label: 'Account Manager' },
  { value: 'customer_success', label: 'Customer Success' },
  { value: 'implementations', label: 'Implementations' },
  { value: 'solutions', label: 'Solutions Engineering' },
  { value: 'admin', label: 'Admin / Operations' },
]

const TEAM_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'presales', label: 'Presales' },
  { value: 'implementations', label: 'Implementations' },
  { value: 'solutions', label: 'Solutions Engineering' },
  { value: 'admin', label: 'Admin / Operations' },
]

const ROLE_BADGE_COLORS: Record<string, string> = {
  presales: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  product_manager: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
  sales: 'bg-green-500/10 text-green-400 border-green-500/30',
  account_manager: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
  customer_success: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  implementations: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  solutions: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30',
  admin: 'bg-gray-500/10 text-gray-400 border-gray-500/30',
}

interface FormState {
  name: string
  email: string
  role: string
  team: string
}

const emptyForm: FormState = { name: '', email: '', role: '', team: '' }

export function TeamMembersTab() {
  const [members, setMembers] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [addForm, setAddForm] = useState<FormState>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editForm, setEditForm] = useState<FormState>(emptyForm)

  const loadMembers = async () => {
    try {
      const res = await teamMembersApi.list()
      if (res.success && res.data) {
        setMembers(res.data.items)
      }
    } catch {
      showToast('Failed to load team members', 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadMembers()
  }, [])

  const handleAdd = async () => {
    if (!addForm.name.trim() || !addForm.email.trim()) {
      showToast('Name and email are required', 'error')
      return
    }

    setSaving(true)
    try {
      const res = await teamMembersApi.create({
        name: addForm.name.trim(),
        email: addForm.email.trim(),
        role: addForm.role.trim() || null,
        team: addForm.team || null,
      })
      if (res.success) {
        showToast('Team member added', 'success')
        setAddForm(emptyForm)
        setShowAddForm(false)
        loadMembers()
      } else {
        showToast(res.error || 'Failed to add team member', 'error')
      }
    } catch {
      showToast('Failed to add team member', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdate = async (id: number) => {
    if (!editForm.name.trim() || !editForm.email.trim()) {
      showToast('Name and email are required', 'error')
      return
    }

    setSaving(true)
    try {
      const res = await teamMembersApi.update(id, {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        role: editForm.role.trim() || null,
        team: editForm.team || null,
      })
      if (res.success) {
        showToast('Team member updated', 'success')
        setEditingId(null)
        loadMembers()
      } else {
        showToast(res.error || 'Failed to update team member', 'error')
      }
    } catch {
      showToast('Failed to update team member', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (member: TeamMember) => {
    try {
      if (member.is_active) {
        const res = await teamMembersApi.deactivate(member.id)
        if (res.success) {
          showToast(`${member.name} deactivated`, 'success')
          loadMembers()
        }
      } else {
        const res = await teamMembersApi.update(member.id, { is_active: true })
        if (res.success) {
          showToast(`${member.name} reactivated`, 'success')
          loadMembers()
        }
      }
    } catch {
      showToast('Failed to update team member', 'error')
    }
  }

  const startEditing = (member: TeamMember) => {
    setEditingId(member.id)
    setEditForm({
      name: member.name,
      email: member.email,
      role: member.role || '',
      team: member.team || '',
    })
  }

  const activeMembers = members.filter(m => m.is_active)
  const inactiveMembers = members.filter(m => !m.is_active)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="text-primary" size={20} />
          <div>
            <h2 className="text-lg font-medium text-[var(--text-primary)]">Team Members</h2>
            <p className="text-sm text-[var(--text-muted)]">
              Manage team members. Assign to &quot;Presales&quot; team to include in dashboard metrics and calendar tracking.
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Plus size={14} />
          Add Member
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="border border-[var(--border-color)] rounded-lg p-4 bg-[var(--bg-secondary)]">
          <h3 className="text-sm font-medium text-[var(--text-primary)] mb-3">New Team Member</h3>
          <div className="grid grid-cols-4 gap-3">
            <input
              type="text"
              placeholder="Full name"
              value={addForm.name}
              onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
              className="px-3 py-2 text-sm border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
            />
            <input
              type="email"
              placeholder="Email address"
              value={addForm.email}
              onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))}
              className="px-3 py-2 text-sm border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
            />
            <select
              value={addForm.role}
              onChange={e => setAddForm(f => ({ ...f, role: e.target.value }))}
              className="px-3 py-2 text-sm border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)]"
            >
              {ROLE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select
              value={addForm.team}
              onChange={e => setAddForm(f => ({ ...f, team: e.target.value }))}
              className="px-3 py-2 text-sm border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)]"
            >
              {TEAM_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button
              onClick={() => { setShowAddForm(false); setAddForm(emptyForm) }}
              className="px-3 py-1.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              <Check size={14} />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Active Members Table */}
      <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--bg-secondary)] border-b border-[var(--border-color)]">
              <th className="text-left px-4 py-2.5 font-medium text-[var(--text-secondary)]">Name</th>
              <th className="text-left px-4 py-2.5 font-medium text-[var(--text-secondary)]">Email</th>
              <th className="text-left px-4 py-2.5 font-medium text-[var(--text-secondary)]">Role</th>
              <th className="text-left px-4 py-2.5 font-medium text-[var(--text-secondary)]">Team</th>
              <th className="text-right px-4 py-2.5 font-medium text-[var(--text-secondary)]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {activeMembers.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-8 text-[var(--text-muted)]">
                  No active team members. Add one above.
                </td>
              </tr>
            )}
            {activeMembers.map(member => (
              <tr key={member.id} className="border-b border-[var(--border-color)] last:border-b-0 hover:bg-[var(--bg-secondary)] transition-colors">
                {editingId === member.id ? (
                  <>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-[var(--border-color)] rounded bg-[var(--bg-primary)] text-[var(--text-primary)]"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-[var(--border-color)] rounded bg-[var(--bg-primary)] text-[var(--text-primary)]"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <select
                        value={editForm.role}
                        onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-[var(--border-color)] rounded bg-[var(--bg-primary)] text-[var(--text-primary)]"
                      >
                        {ROLE_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <select
                        value={editForm.team}
                        onChange={e => setEditForm(f => ({ ...f, team: e.target.value }))}
                        className="w-full px-2 py-1 text-sm border border-[var(--border-color)] rounded bg-[var(--bg-primary)] text-[var(--text-primary)]"
                      >
                        {TEAM_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleUpdate(member.id)}
                          disabled={saving}
                          className="p-1.5 text-green-500 hover:bg-green-500/10 rounded transition-colors"
                          title="Save"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="p-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded transition-colors"
                          title="Cancel"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2.5 text-[var(--text-primary)] font-medium">{member.name}</td>
                    <td className="px-4 py-2.5 text-[var(--text-secondary)]">{member.email}</td>
                    <td className="px-4 py-2.5">
                      {member.role && (
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${
                          ROLE_BADGE_COLORS[member.role] || 'bg-gray-500/10 text-gray-400 border-gray-500/30'
                        }`}>
                          {ROLE_OPTIONS.find(o => o.value === member.role)?.label || member.role}
                        </span>
                      )}
                      {!member.role && <span className="text-[var(--text-muted)]">—</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      {member.team && (
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${
                          member.team === 'presales' ? 'bg-blue-500/10 text-blue-400 border-blue-500/30' :
                          member.team === 'implementations' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' :
                          'bg-gray-500/10 text-gray-400 border-gray-500/30'
                        }`}>
                          {TEAM_OPTIONS.find(o => o.value === member.team)?.label || member.team}
                        </span>
                      )}
                      {!member.team && <span className="text-[var(--text-muted)]">—</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => startEditing(member)}
                          className="p-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded transition-colors"
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => handleToggleActive(member)}
                          className="p-1.5 text-[var(--text-muted)] hover:text-orange-500 hover:bg-orange-500/10 rounded transition-colors"
                          title="Deactivate"
                        >
                          <UserX size={14} />
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Inactive Members */}
      {inactiveMembers.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-[var(--text-muted)] mb-2">Inactive Members</h3>
          <div className="border border-[var(--border-color)] rounded-lg overflow-hidden opacity-60">
            <table className="w-full text-sm">
              <tbody>
                {inactiveMembers.map(member => (
                  <tr key={member.id} className="border-b border-[var(--border-color)] last:border-b-0">
                    <td className="px-4 py-2 text-[var(--text-secondary)]">{member.name}</td>
                    <td className="px-4 py-2 text-[var(--text-muted)]">{member.email}</td>
                    <td className="px-4 py-2 text-[var(--text-muted)]">{member.role || '—'}</td>
                    <td className="px-4 py-2 text-[var(--text-muted)]">{member.team || '—'}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-end">
                        <button
                          onClick={() => handleToggleActive(member)}
                          className="p-1.5 text-[var(--text-muted)] hover:text-green-500 hover:bg-green-500/10 rounded transition-colors"
                          title="Reactivate"
                        >
                          <UserCheck size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
